/* ============================================
   Landing de descarga de backup (pública)
   Ruta: /backup/:token
   ============================================ */

import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'

interface BackupInfo {
  dominio: string
  plan_nombre: string
  nombre: string
  size_mb: string
  fecha: string
  download_url: string
  status?: string
}

export default function DescargarBackup() {
  const { token } = useParams<{ token: string }>()
  const api = useApi()
  const [info, setInfo] = useState<BackupInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return
    api.get<BackupInfo>(`/api/hosting/backup/${token}`)
      .then(data => {
        if (data.status === 'generating') {
          setError('La copia de seguridad aún se está generando. Recibirás un email cuando esté lista.')
        } else {
          setInfo(data)
        }
      })
      .catch(() => setError('Backup no encontrado o el enlace ha expirado.'))
      .finally(() => setLoading(false))
  }, [token])

  if (loading) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    }}>
      <div style={{ textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 16px', width: 40, height: 40 }} />
        <p style={{ color: '#6B7280' }}>Cargando...</p>
      </div>
    </div>
  )

  if (error) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    }}>
      <div style={{
        background: '#fff', borderRadius: 24, padding: '48px 32px', maxWidth: 440, width: '90%',
        textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#0C1E2C', marginBottom: 12 }}>
          Backup no disponible
        </h1>
        <p style={{ color: '#6B7280', lineHeight: 1.6, fontSize: 15 }}>
          {error}
        </p>
        <p style={{ color: '#9CA3AF', fontSize: 13, marginTop: 24 }}>
          Si crees que esto es un error, contáctanos en{' '}
          <a href="mailto:soporte@1conecta.com" style={{ color: '#0064E0' }}>soporte@1conecta.com</a>
        </p>
      </div>
    </div>
  )

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 50%, #dbe9ff 100%)',
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      padding: 24,
    }}>
      <div style={{
        background: '#fff', borderRadius: 24, padding: '48px 32px', maxWidth: 480, width: '100%',
        boxShadow: '0 4px 32px rgba(0,100,224,0.1)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 32 }}>
          <img
            src="/assets/brand/logo-full-color.svg"
            alt="1Conecta"
            style={{ height: 28, width: 'auto', display: 'block', marginBottom: 32 }}
          />
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0C1E2C', margin: '0 0 6px', letterSpacing: '-0.01em' }}>
            Copia de seguridad
          </h1>
          <p style={{ color: '#6B7280', fontSize: 15, margin: 0, fontWeight: 500 }}>
            {info?.dominio || info?.plan_nombre}
          </p>
        </div>

        <div style={{
          background: '#F5F7F9', borderRadius: 16, padding: 20, marginBottom: 28,
        }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6B7280', fontSize: 14 }}>Plan</span>
              <span style={{ fontWeight: 600, color: '#0C1E2C', fontSize: 14 }}>{info?.plan_nombre}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6B7280', fontSize: 14 }}>Dominio</span>
              <span style={{ fontWeight: 600, color: '#0C1E2C', fontSize: 14 }}>{info?.dominio || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6B7280', fontSize: 14 }}>Tamaño</span>
              <span style={{ fontWeight: 600, color: '#0C1E2C', fontSize: 14 }}>{info?.size_mb} MB</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6B7280', fontSize: 14 }}>Fecha</span>
              <span style={{ fontWeight: 600, color: '#0C1E2C', fontSize: 14 }}>
                {info?.fecha ? new Date(info.fecha).toLocaleDateString('es-PE', {
                  year: 'numeric', month: 'long', day: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                }) : '—'}
              </span>
            </div>
          </div>
        </div>

        <a
          href={info?.download_url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            width: '100%', padding: '16px 0',
            background: 'linear-gradient(135deg, #0064E0, #2563EB)',
            color: '#fff', textDecoration: 'none', borderRadius: 16,
            fontWeight: 600, fontSize: 16,
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLAnchorElement).style.transform = 'scale(1.02)'
            ;(e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 8px 24px rgba(0,100,224,0.3)'
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.transform = 'scale(1)'
            ;(e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Descargar backup
        </a>

        <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12, marginTop: 20 }}>
          Este enlace es válido por 30 días.
        </p>
      </div>
    </div>
  )
}
